import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import styles from './PersonalDashboard.module.css';
import routes from 'routes';

import Loading from 'components/common/Loading';
import LabelText from 'components/common/LabelText';
import CustomButton, {
  defaultButtonStyle,
} from 'components/common/CustomButton';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';
import ProfileEditModal from 'components/personal/ProfileEditModal';
import AthleteEditModal from 'components/personal/AthleteEditModal';
import SecondaryContactEditModal from 'components/personal/SecondaryContactEditModal';

import {
  getMyDashboard,
  removeMyAthleteFromClub,
  deleteMyAthlete,
  removeMySecondaryContact,
  sendInvitation,
} from 'api/userApi';
import { formatExpiresRelative } from 'utils/time';
import {
  GROUPING_BASIS,
  UNDER_BAND_LAST_AGE,
  groupingBasisOf,
  groupWords,
  formatGroupCell,
  formatGroupHeading,
  valueHeadingFor,
} from 'utils/groupLabel';

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

// ISO yyyy-MM-dd (DateOnly) → "12 Mar 2014".
const formatDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  const mi = parseInt(m, 10) - 1;
  return `${parseInt(d, 10)} ${MONTHS[mi] || m} ${y}`;
};

// Club names are stored "... Club"; show them without the trailing word.
const trimClubName = (name) => (name ? name.replace(/\s+Club$/i, '') : name);

// Athlete membership type: a "Full" athlete is shown as "Member".
const membershipLabel = (type) => (type === 'Full' ? 'Member' : type);

// Guest/visitor = manageable by the family; Full = club-managed.
const isGuestClass = (type) => type === 'Guest' || type === 'Visitor';

// Colour-coded badge for a membership level / type (Member · Guest · Visitor; others neutral).
const membershipBadge = (label) => {
  const key = (label || '').toLowerCase();
  const cls =
    key === 'member'
      ? styles.badgeMember
      : key === 'guest'
        ? styles.badgeGuest
        : key === 'visitor'
          ? styles.badgeVisitor
          : styles.badgeNeutral;
  return <span className={`${styles.badge} ${cls}`}>{label}</span>;
};

const statusEl = (active) =>
  active ? (
    <span className={styles.active}>Active</span>
  ) : (
    <span className={styles.inactive}>Inactive</span>
  );

// Card-header button styles — match the admin Member Information view (our design model):
// small CustomButtons built off defaultButtonStyle.
const EDIT_BTN_STYLE = {
  ...defaultButtonStyle,
  marginBottom: 0,
  height: 'fit-content',
  padding: '4px 12px',
  fontSize: '13px',
  backgroundColor: '#3b82f6',
};
const REMOVE_BTN_STYLE = {
  ...defaultButtonStyle,
  marginBottom: 0,
  height: 'fit-content',
  padding: '4px 12px',
  fontSize: '13px',
  backgroundColor: '#cf2c47',
};
const ADD_BTN_STYLE = {
  ...defaultButtonStyle,
  marginBottom: 0,
  height: 'fit-content',
  padding: '4px 12px',
  fontSize: '13px',
};

const NO_CONFIRM = {
  open: false,
  title: '',
  message: '',
  confirmText: 'Remove',
  confirmVariant: 'danger',
  successMsg: '',
  busy: false,
  action: null,
};

const PersonalDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [athleteModal, setAthleteModal] = useState({ open: false, athlete: null });
  const [secondaryModal, setSecondaryModal] = useState({ open: false, mode: 'add' });
  const [confirm, setConfirm] = useState(NO_CONFIRM);

  const load = useCallback(async () => {
    try {
      const res = await getMyDashboard();
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not load your dashboard.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // A confirm-then-act helper for the destructive actions (remove from club / delete / remove secondary)
  // and the invite confirm (confirmVariant 'primary').
  const ask = ({
    title,
    message,
    confirmText,
    confirmVariant = 'danger',
    successMsg,
    action,
  }) => {
    setConfirm({
      open: true,
      title,
      message,
      confirmText,
      confirmVariant,
      successMsg,
      busy: false,
      action,
    });
  };

  const runConfirm = async () => {
    if (!confirm.action) return;
    setConfirm((c) => ({ ...c, busy: true }));
    try {
      await confirm.action();
      toast.success(confirm.successMsg || 'Done.');
      setConfirm(NO_CONFIRM);
      load();
    } catch (e) {
      toast.error(
        e.response?.data?.message || e.response?.data || 'Could not complete that action.'
      );
      setConfirm((c) => ({ ...c, busy: false }));
    }
  };

  if (loading) return <Loading />;
  if (!data) return null;

  const { primaryContact, secondaryContact, athletes, canEditEmail } = data;
  const youContact =
    [primaryContact, secondaryContact].find((c) => c && c.isYou) ||
    primaryContact;

  // The whole family view is read-only unless the logged-in user is the family's PRIMARY contact —
  // a secondary contact can edit nothing (personal-portal §4.6).
  const amIPrimary = primaryContact?.isYou === true;
  const activeClubIds = (primaryContact?.clubs || [])
    .filter((c) => c.active)
    .map((c) => c.clubId);

  // One row per (athlete, club); identity shown once per athlete group.
  const athleteRows = [];
  athletes.forEach((a) => {
    if (a.clubs.length === 0) {
      athleteRows.push({ athlete: a, club: null, first: true });
    } else {
      a.clubs.forEach((cl, i) =>
        athleteRows.push({ athlete: a, club: cl, first: i === 0 })
      );
    }
  });

  // The athletes can be in clubs of both kinds, so the column is headed by the clubs in the rows: Age, Year,
  // or "Age / Year" when the family has athletes in both. Each row carries its own club's grouping basis.
  const groupColumnHeading = valueHeadingFor(
    athleteRows.filter((row) => row.club).map((row) => groupingBasisOf(row.club))
  );

  // Upcoming meet registrations across all clubs → the "Multi-club Meets & Carnivals" panel.
  const now = new Date();
  const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
    2,
    '0'
  )}-${String(now.getDate()).padStart(2, '0')}`;
  const meetRows = [];
  athletes.forEach((a) => {
    const upcoming = [];
    (a.clubs || []).forEach((cl) => {
      (cl.meets || []).forEach((m) => {
        if (m.meetDate && m.meetDate >= todayIso) {
          upcoming.push({ club: cl, meet: m });
        }
      });
    });
    upcoming.sort((x, y) =>
      x.meet.meetDate < y.meet.meetDate
        ? -1
        : x.meet.meetDate > y.meet.meetDate
          ? 1
          : 0
    );
    upcoming.forEach((row, i) =>
      meetRows.push({ athlete: a, club: row.club, meet: row.meet, first: i === 0 })
    );
  });

  // Destructive-action launchers.
  const askRemoveFromClub = (a, cl) =>
    ask({
      title: 'Remove from club',
      message: `Remove ${a.firstName} ${a.lastName} from ${trimClubName(
        cl.clubName
      )}? Their past results stay with the club.`,
      confirmText: 'Remove',
      successMsg: 'Removed from club.',
      action: () => removeMyAthleteFromClub(a.athleteProfileId, cl.clubId),
    });

  const askDeleteAthlete = (a) =>
    ask({
      title: 'Delete athlete',
      message: `Delete ${a.firstName} ${a.lastName}'s profile? This can't be undone.`,
      confirmText: 'Delete',
      successMsg: 'Athlete deleted.',
      action: () => deleteMyAthlete(a.athleteProfileId),
    });

  const askRemoveSecondary = () =>
    ask({
      title: 'Remove secondary contact',
      message: secondaryContact
        ? `Remove ${secondaryContact.firstName} ${secondaryContact.lastName} as a secondary contact? They keep any club memberships.`
        : '',
      confirmText: 'Remove',
      successMsg: 'Secondary contact removed.',
      action: () => removeMySecondaryContact(),
    });

  // Invite (or re-invite) the secondary to SplitFast — same as the admin Member Information view. The
  // invite is sent in the context of a club the secondary is an active member of; the server allows the
  // family's primary to do this.
  const askInviteSecondary = (isResend) => {
    const club = (secondaryContact?.clubs || []).find((c) => c.active);
    if (!club) {
      toast.error(
        'The secondary contact must be an active member of a club before they can be invited.'
      );
      return;
    }
    ask({
      title: isResend ? 'Resend invitation' : 'Send invitation',
      message: `${isResend ? 'Resend' : 'Send'} a SplitFast invitation email to ${
        secondaryContact.firstName
      } ${secondaryContact.lastName}? They'll be asked to confirm their email and set a password.`,
      confirmText: isResend ? 'Resend invite' : 'Send invitation',
      confirmVariant: 'primary',
      successMsg: 'Invitation sent.',
      action: () => sendInvitation(club.clubId, secondaryContact.email),
    });
  };

  // Invite / Re-invite button for the secondary (only the primary can send it, and only when the
  // secondary hasn't accepted yet and is an active member somewhere). Orange = resend, red = first invite.
  const secondaryHasActiveClub = (secondaryContact?.clubs || []).some(
    (c) => c.active
  );
  const secondaryInviteButton =
    amIPrimary &&
    secondaryContact &&
    secondaryContact.invitationStatus !== 'Accepted' &&
    secondaryHasActiveClub ? (
      <CustomButton
        style={{
          ...defaultButtonStyle,
          marginBottom: 0,
          height: 'fit-content',
          padding: '4px 16px',
          fontSize: '13px',
          backgroundColor:
            secondaryContact.invitationStatus === 'Pending'
              ? '#f59e0b'
              : '#cf2c47',
        }}
        onClick={() =>
          askInviteSecondary(secondaryContact.invitationStatus === 'Pending')
        }
        title={
          secondaryContact.invitationStatus === 'Pending'
            ? 'Resend invitation email'
            : 'Send invitation email'
        }
      >
        {secondaryContact.invitationStatus === 'Pending'
          ? 'Resend Invite'
          : 'Invite'}
      </CustomButton>
    ) : null;

  // Platform-access row shown on the secondary card (status + invite button) — mirrors the admin
  // "Portal Access" field.
  const secondaryPlatformRow = secondaryContact ? (
    <div className={styles.platformRow}>
      <div>
        <div className={styles.platformLabel}>Platform Access</div>
        <div className={styles.platformValue}>
          {secondaryContact.invitationStatus}
        </div>
      </div>
      {secondaryInviteButton}
    </div>
  ) : null;

  const renderContactCard = (contact, title, actions, belowInfo) => (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>{title}</h3>
        {actions}
      </div>
      <div className={styles.cardContent}>
        {contact ? (
          <>
            <div className={styles.infoGrid}>
              <LabelText
                label="Name"
                text={`${contact.firstName} ${contact.lastName}${
                  contact.isYou ? ' (you)' : ''
                }`}
              />
              <LabelText label="Email" text={contact.email} />
            </div>
            {belowInfo}
            {contact.clubs.length > 0 ? (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Club</th>
                    <th>Level</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {contact.clubs.map((cl) => (
                    <tr key={cl.clubId}>
                      <td>{trimClubName(cl.clubName)}</td>
                      <td>{membershipBadge(cl.level)}</td>
                      <td>{statusEl(cl.active)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className={styles.subtle}>Not a member of any club.</div>
            )}
          </>
        ) : (
          <div className={styles.emptyState}>No secondary contact added</div>
        )}
      </div>
    </div>
  );

  return (
    <div className={styles.page}>
      {renderContactCard(
        primaryContact,
        'Primary Contact',
        primaryContact?.isYou ? (
          <CustomButton
            style={EDIT_BTN_STYLE}
            onClick={() => setEditOpen(true)}
            title="Edit your details"
          >
            Edit
          </CustomButton>
        ) : null
      )}

      {renderContactCard(
        secondaryContact,
        'Secondary Contact',
        amIPrimary ? (
          secondaryContact ? (
            <div className={styles.headerActions}>
              {activeClubIds.length > 0 && (
                <CustomButton
                  style={EDIT_BTN_STYLE}
                  onClick={() => setSecondaryModal({ open: true, mode: 'edit' })}
                  title="Edit this secondary contact's details"
                >
                  Edit
                </CustomButton>
              )}
              <CustomButton
                style={REMOVE_BTN_STYLE}
                onClick={askRemoveSecondary}
                title="Remove the secondary contact from this family"
              >
                Remove
              </CustomButton>
            </div>
          ) : activeClubIds.length > 0 ? (
            <CustomButton
              style={ADD_BTN_STYLE}
              onClick={() => setSecondaryModal({ open: true, mode: 'add' })}
              title="Add a secondary contact for this family"
            >
              + Add secondary contact
            </CustomButton>
          ) : null
        ) : null,
        secondaryPlatformRow
      )}

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h3 className={styles.cardTitle}>Athletes</h3>
          {amIPrimary && (
            <button
              type="button"
              className={styles.addIconBtn}
              onClick={() => setAthleteModal({ open: true, athlete: null })}
              title="Add New Athlete Profile"
            >
              ➕
            </button>
          )}
        </div>
        <div className={styles.cardContent}>
          {/* §4.4: full-member athletes are club-managed, so their rows have no edit/remove actions.
              Say so once for the whole card rather than leaving the empty Actions cells unexplained. */}
          {athletes.some((a) => !a.isEditable) && (
            <p className={styles.cardNote}>
              Athletes with a full club membership are managed by their club. To
              change their details, please contact your club.
            </p>
          )}
          {athletes.length === 0 ? (
            <div className={styles.emptyState}>No athlete profiles added</div>
          ) : (
            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Club</th>
                    <th>Membership</th>
                    <th>Active</th>
                    <th>Bib</th>
                    <th>{groupColumnHeading}</th>
                    <th>Expires</th>
                    <th className={styles.actionsCol}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {athleteRows.map((row) => {
                    const a = row.athlete;
                    const cl = row.club;
                    return (
                      <tr
                        key={`${a.athleteProfileId}-${cl ? cl.clubId : 'none'}`}
                        className={row.first ? styles.groupStart : ''}
                      >
                        <td>
                          {row.first && (
                            <>
                              <div className={styles.athleteName}>
                                {a.firstName} {a.lastName}
                              </div>
                              <div className={styles.athleteSub}>
                                {a.gender}
                                {a.birthDate ? ` · ${formatDate(a.birthDate)}` : ''}
                              </div>
                            </>
                          )}
                        </td>
                        {cl ? (
                          <>
                            <td>{trimClubName(cl.clubName)}</td>
                            <td>{membershipBadge(membershipLabel(cl.membershipType))}</td>
                            <td>{statusEl(cl.active)}</td>
                            <td>{cl.bibNumber != null ? cl.bibNumber : '—'}</td>
                            <td>
                              {/* A club that groups by year level: the year (Y6). Its competing age is not an age
                                  to show, so the rules below are for a club that groups by age.
                                  An Age club: the number is the competing age at every age, and nothing reads "U".
                                  An Under club: while the athlete is in the under-band, which runs to U20, the
                                  number is the club age group (U-group, admin-roster convention); from a competing
                                  age of 20 there is no U-group, so show the season competing age instead. */}
                              {groupingBasisOf(cl) === GROUPING_BASIS.YEAR_LEVEL ? (
                                cl.ageGroup ? (
                                  <span
                                    title={`${groupWords(groupingBasisOf(cl)).sentence.group} for this season (${formatGroupHeading(groupingBasisOf(cl), cl.ageGroup)})`}
                                  >
                                    {formatGroupCell(groupingBasisOf(cl), cl.ageGroup)}
                                  </span>
                                ) : (
                                  '—'
                                )
                              ) : groupingBasisOf(cl) === GROUPING_BASIS.AGE ? (
                                cl.ageGroup ? (
                                  <span title="Competing age for this season.">
                                    {cl.ageGroup}
                                  </span>
                                ) : (
                                  '—'
                                )
                              ) : cl.competingAge != null && cl.competingAge > UNDER_BAND_LAST_AGE ? (
                                <span title="Competing age this season. Athletes 20 and over compete by age rather than in a club age group.">
                                  {cl.competingAge}
                                </span>
                              ) : cl.ageGroup ? (
                                <span
                                  title={`Club age group for this season (${formatGroupHeading(groupingBasisOf(cl), cl.ageGroup)})`}
                                >
                                  {cl.ageGroup}
                                </span>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td>
                              {cl.expiresAt
                                ? formatExpiresRelative(cl.expiresAt)
                                : '—'}
                            </td>
                          </>
                        ) : (
                          <td colSpan={6} className={styles.subtle}>
                            Not in a club yet
                          </td>
                        )}
                        <td className={styles.actionsCol}>
                          {amIPrimary && (
                            <div className={styles.actions}>
                              {row.first && a.isEditable && (
                                <button
                                  type="button"
                                  className={styles.actionIcon}
                                  title="Edit athlete"
                                  onClick={() =>
                                    setAthleteModal({ open: true, athlete: a })
                                  }
                                >
                                  ✏️
                                </button>
                              )}
                              {cl && isGuestClass(cl.membershipType) && (
                                <button
                                  type="button"
                                  className={styles.actionIcon}
                                  title={`Remove from ${trimClubName(cl.clubName)}`}
                                  onClick={() => askRemoveFromClub(a, cl)}
                                >
                                  🗑️
                                </button>
                              )}
                              {row.first && a.clubs.length === 0 && (
                                <button
                                  type="button"
                                  className={styles.actionIcon}
                                  title="Delete athlete"
                                  onClick={() => askDeleteAthlete(a)}
                                >
                                  🗑️
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {meetRows.length > 0 && (
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h3 className={styles.cardTitle}>Multi-club Meets &amp; Carnivals</h3>
          </div>
          <div className={styles.cardContent}>
            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Athlete</th>
                    <th>Club</th>
                    <th>Date</th>
                    <th>Meet</th>
                  </tr>
                </thead>
                <tbody>
                  {meetRows.map((row) => (
                    <tr
                      key={`${row.athlete.athleteProfileId}-${row.meet.meetId}`}
                      className={row.first ? styles.groupStart : ''}
                    >
                      <td>
                        {row.first
                          ? `${row.athlete.firstName} ${row.athlete.lastName}`
                          : ''}
                      </td>
                      <td>{trimClubName(row.club.clubName)}</td>
                      <td>{formatDate(row.meet.meetDate)}</td>
                      <td>
                        <Link
                          to={routes.admin.showMeet.url(
                            row.club.clubId,
                            row.meet.meetId
                          )}
                          className={styles.meetLink}
                        >
                          {row.meet.meetName}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <ProfileEditModal
        isOpen={editOpen}
        contact={youContact}
        canEditEmail={canEditEmail}
        onClose={() => setEditOpen(false)}
        onSaved={() => {
          setEditOpen(false);
          load();
        }}
      />

      <AthleteEditModal
        isOpen={athleteModal.open}
        athlete={athleteModal.athlete}
        onClose={() => setAthleteModal({ open: false, athlete: null })}
        onSaved={() => {
          setAthleteModal({ open: false, athlete: null });
          load();
        }}
      />

      <SecondaryContactEditModal
        isOpen={secondaryModal.open}
        mode={secondaryModal.mode}
        primaryUserId={primaryContact?.userId}
        secondary={secondaryContact}
        clubIds={activeClubIds}
        onClose={() => setSecondaryModal({ open: false, mode: 'add' })}
        onSaved={() => {
          setSecondaryModal({ open: false, mode: 'add' });
          load();
        }}
      />

      <CustomNewConfirm
        isOpen={confirm.open}
        title={confirm.title}
        description={confirm.message}
        confirmButtonText={confirm.busy ? 'Working…' : confirm.confirmText}
        confirmButtonVariant={confirm.confirmVariant}
        cancelButtonText="Cancel"
        isLoading={confirm.busy}
        onConfirm={runConfirm}
        onClose={() => setConfirm(NO_CONFIRM)}
      />
    </div>
  );
};

export default PersonalDashboard;
